import { ARENA } from './balance'
import { drawLampMote, lampMoteOld } from './lamp_mote'
import { falloffGlow, lampFlicker, lampLightV1 } from './lamp_light'
import type { AdvLampGlow, AdvStatic, AdvWind } from './adv'

// 絵の上に重ねる空気の層 (2026-10-30 プロローグ)。
//   常夜燈の漂い＝青白い光の霞と粒が、 出どころの分からないまま城のまわりを漂う。 常夜燈そのもの (灯りの点) は描かない
//     (常夜燈の位置は 06-24 の裁定で背景でも隠している)。 色は青っぽく (10-11 作者「常夜燈の色は青っぽい色」)。
//   風＝1枚の絵 (外套) が、 左から吹く風にちぎられて右へ流れて消える。 下の城門は動かさず、 ゆっくり薄れるだけ。
// どちらも状態を持たず、 番号の hash と時刻だけで決める。
const hash = (i: number, k: number) => { const s = Math.sin(i * 127.1 + k * 311.7) * 43758.5453; return s - Math.floor(s) }
const LAMP = [150, 196, 255] as const, MOTE = [200, 226, 255] as const

export function drawAdvLampGlow(ctx: CanvasRenderingContext2D, v: AdvLampGlow, now: number): void {
  if (v.a <= 0) return
  const cx = v.x * ARENA.width, cy = v.y * ARENA.height, RX = v.rx * ARENA.width, RY = v.ry * ARENA.height
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  // 霞: 大きく淡い塊がゆっくり流れ、 濃さが息をするように揺れる。
  for (let k = 0; k < 11; k++) {
    const sp = .00006 + hash(k, 1) * .00008
    const bx = cx + RX * ((hash(k, 2) * 2 - 1) * .85 + .22 * Math.sin(now * sp + k * 1.7))
    const by = cy + RY * ((hash(k, 3) * 2 - 1) * .75 + .14 * Math.cos(now * sp * 1.3 + k))
    const r = 70 + hash(k, 4) * 120
    // 比較版 v1 (11-03・src/lamp_light.ts): 縁の見えない減り方 (芯から距離の2乗で減る) と、 塊ごとにずれた不規則な揺らぎ。
    //   濃さは 11-03 作者「ちょっと明るすぎるな。」で .1+.1 → .065+.065 (灯りのにじみと同じ 0.65 倍)。
    if (lampLightV1()) { falloffGlow(ctx, bx, by, r * 1.3, r * .35, LAMP, v.a * (.065 + .065 * hash(k, 5)) * lampFlicker(now / 1000 * .6, 10 + k)); continue }
    const a = v.a * (.05 + .05 * hash(k, 5)) * (.65 + .35 * Math.sin(now * .0005 + k * 2.3))
    const g = ctx.createRadialGradient(bx, by, 0, bx, by, r)
    g.addColorStop(0, `rgba(${LAMP[0]},${LAMP[1]},${LAMP[2]},${a})`); g.addColorStop(1, `rgba(${LAMP[0]},${LAMP[1]},${LAMP[2]},0)`)
    ctx.fillStyle = g; ctx.fillRect(bx - r, by - r, r * 2, r * 2)
  }
  // 粒: ふわりと上へ漂い、 瞬きながら生まれて消える (どこか1点から湧かない)。
  for (let k = 0; k < 90; k++) {
    const period = 7000 + hash(k, 6) * 6000, phase = (now / period + hash(k, 7)) % 1
    const loop = Math.floor(now / period + hash(k, 7))
    const bx = cx + RX * (hash(k + loop * 97, 8) * 2 - 1), by = cy + RY * (hash(k + loop * 97, 9) * 2 - 1) * .9
    const v1 = lampLightV1()
    // 比較版 v1: 昇るほど熱で横に揺れが大きくなり、 少しずつ速く昇る。 瞬きは灯りと同じ不規則な揺らぎ。
    const x = bx + (v1 ? (4 + phase * 14) * Math.sin(now * (.0011 + hash(k, 14) * .0008) + k * 2.1) : 14 * Math.sin(now * .0007 + k)) + phase * (hash(k, 10) - .5) * 40
    const y = by - (v1 ? phase ** 1.3 : phase) * (25 + hash(k, 11) * 50)
    const size = .7 + hash(k, 12) * 1.6
    const twinkle = v1 ? Math.min(1, (lampFlicker(now / 1000, 40 + k) - .62) * 2.2) : .55 + .45 * Math.sin(now * (.002 + hash(k, 13) * .004) + k)
    const a = v.a * Math.sin(Math.PI * phase) * twinkle * .85
    if (a <= .01) continue
    // 1粒は常夜燈の光の正本 (src/lamp_mote.ts・10-31)。前の版 (芯 MOTE・にじみ LAMP・大きさ .7〜2.3 の4倍) = DEV ?lampMote=old。
    if (!lampMoteOld()) { drawLampMote(ctx, x, y, a); continue }
    const g = ctx.createRadialGradient(x, y, 0, x, y, size * 4)
    g.addColorStop(0, `rgba(${MOTE[0]},${MOTE[1]},${MOTE[2]},${a})`); g.addColorStop(.35, `rgba(${LAMP[0]},${LAMP[1]},${LAMP[2]},${a * .35})`)
    g.addColorStop(1, `rgba(${LAMP[0]},${LAMP[1]},${LAMP[2]},0)`)
    ctx.fillStyle = g; ctx.fillRect(x - size * 4, y - size * 4, size * 8, size * 8)
  }
  ctx.restore()
}

// --- 風 ---
const W = 160, H = 90
let noise: Float32Array | null = null
function noiseField(): Float32Array {
  if (noise) return noise
  let seed = 11
  const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647 }
  const out = new Float32Array(W * H)
  for (const [cell, weight] of [[12, .5], [6, .3], [3, .2]] as const) {
    const gw = Math.ceil(W / cell) + 2, gh = Math.ceil(H / cell) + 2
    const grid = Array.from({ length: gw * gh }, rnd)
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      const fx = x / cell, fy = y / cell, ix = Math.floor(fx), iy = Math.floor(fy)
      const sx = (fx - ix) ** 2 * (3 - 2 * (fx - ix)), sy = (fy - iy) ** 2 * (3 - 2 * (fy - iy))
      const g = (i: number, j: number) => grid[j * gw + i]!
      const top = g(ix, iy) + (g(ix + 1, iy) - g(ix, iy)) * sx, bottom = g(ix, iy + 1) + (g(ix + 1, iy + 1) - g(ix, iy + 1)) * sx
      out[y * W + x]! += (top + (bottom - top) * sy) * weight
    }
  }
  return (noise = out)
}
type Layer = { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D }
const layers: Layer[] = []
let maskData: ImageData | null = null
function layer(i: number, w: number, h: number): Layer | null {
  if (typeof document === 'undefined') return null
  let l = layers[i]
  if (!l) {
    const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h
    const c = canvas.getContext('2d'); if (!c) return null
    l = layers[i] = { canvas, ctx: c }
  }
  return l
}
// 外套がかかっているのは絵の上のほう (空)。 ここより下 (城門) は流さない。
const CLOTH_TOP = .3, CLOTH_FADE = .2
const EDGE = .12
const easeIn = (t: number) => t * t * (3 - 2 * t) * t
function coverRect(img: HTMLImageElement) {
  const s = Math.max(ARENA.width / img.naturalWidth, ARENA.height / img.naturalHeight)
  const w = img.naturalWidth * s, h = img.naturalHeight * s
  return { x: (ARENA.width - w) / 2, y: (ARENA.height - h) / 2, w, h }
}

export function drawAdvWind(ctx: CanvasRenderingContext2D, img: HTMLImageElement, v: AdvWind): void {
  const out = v.dir === 'out'
  // gone＝外套が消えた割合 (out は進むほど・in は進むほど小さく)。
  const gone = out ? v.p : 1 - v.p
  if (gone >= 1) return
  const rect = coverRect(img)
  if (gone <= 0) { ctx.drawImage(img, rect.x, rect.y, rect.w, rect.h); return }
  const work = layer(0, ARENA.width, ARENA.height), mask = layer(1, W, H)
  if (!work || !mask) return
  const n = noiseField()
  // 1) 城門 (下): 動かさずに薄れる。 上へ行くほど外套の側へ渡す。
  const wc = work.ctx
  wc.globalCompositeOperation = 'source-over'; wc.globalAlpha = 1
  wc.clearRect(0, 0, ARENA.width, ARENA.height)
  wc.drawImage(img, rect.x, rect.y, rect.w, rect.h)
  const down = wc.createLinearGradient(0, ARENA.height * CLOTH_TOP, 0, ARENA.height * (CLOTH_TOP + CLOTH_FADE))
  down.addColorStop(0, 'rgba(0,0,0,0)'); down.addColorStop(1, 'rgba(0,0,0,1)')
  wc.globalCompositeOperation = 'destination-in'; wc.fillStyle = down; wc.fillRect(0, 0, ARENA.width, ARENA.height)
  ctx.save()
  ctx.globalAlpha = Math.max(0, 1 - gone * 1.1)
  ctx.drawImage(work.canvas, 0, 0)
  ctx.restore()
  // 2) 外套 (上): 風下 (右上) へ流れながら、 風上 (左) の端からちぎれていく (in は左の縁から現れる)。 ちぎれ方は布に貼りついた模様 (布と一緒に動く)。
  // in は風上 (左) から入ってきて止まる。
  const push = easeIn(gone)
  const dx = ARENA.width * .42 * push * (out ? 1 : -1), dy = -ARENA.height * .07 * push, stretch = 1 + .12 * push
  const T = gone * 1.25 - .12
  const md = maskData ??= mask.ctx.createImageData(W, H)
  const px = md.data
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x
    // out は左ほど先にちぎれる (風を正面に受ける縁から)。 in は左の縁から先に現れる (風に乗って吹き込む)。
    const o = .58 * (out ? x / W : 1 - x / W) + .12 * (y / H) + .3 * n[i]!
    const a = Math.min(1, Math.max(0, (o - T) / EDGE))
    px[i * 4] = 0; px[i * 4 + 1] = 0; px[i * 4 + 2] = 0; px[i * 4 + 3] = a * 255
  }
  mask.ctx.putImageData(md, 0, 0)
  wc.globalCompositeOperation = 'source-over'
  wc.clearRect(0, 0, ARENA.width, ARENA.height)
  wc.save()
  wc.translate(dx, dy); wc.scale(stretch, 1)
  wc.drawImage(img, rect.x, rect.y, rect.w, rect.h)
  wc.globalCompositeOperation = 'destination-in'
  wc.imageSmoothingEnabled = true
  wc.drawImage(mask.canvas, 0, 0, ARENA.width, ARENA.height)
  wc.restore()
  // 布だけを残す (城門の段は上で描いたので、 ここでは上の空の帯だけ)。
  const up = wc.createLinearGradient(0, ARENA.height * CLOTH_TOP, 0, ARENA.height * (CLOTH_TOP + CLOTH_FADE))
  up.addColorStop(0, 'rgba(0,0,0,1)'); up.addColorStop(1, 'rgba(0,0,0,0)')
  wc.globalCompositeOperation = 'destination-in'; wc.fillStyle = up; wc.fillRect(0, 0, ARENA.width, ARENA.height)
  wc.globalCompositeOperation = 'source-over'
  ctx.drawImage(work.canvas, 0, 0)
  // 3) ちぎれた布の切れ端 (out だけ): ちぎれた縁から生まれ、 回りながら右上へ飛ぶ。
  ctx.save()
  const count = out ? 70 : 0, life = .3
  for (let k = 0; k < count; k++) {
    const born = .04 + hash(k, 1) * .78, age = (gone - born) / life
    if (age <= 0 || age >= 1) continue
    // 生まれた時のちぎれる縁のあたり (布と一緒に流れた先)。
    const pb = easeIn(born)
    const ox = ((born * 1.25 - .12 - .3 * hash(k, 2)) / .58) * ARENA.width
    const oy = (.02 + hash(k, 3) * (CLOTH_TOP + .05)) * ARENA.height
    const x0 = ox * (1 + .12 * pb) + ARENA.width * .42 * pb, y0 = oy - ARENA.height * .07 * pb
    const x = x0 + age * (180 + hash(k, 4) * 260), y = y0 - age * (30 + hash(k, 5) * 70) + Math.sin(age * 9 + k) * 8
    const rot = age * (4 + hash(k, 6) * 7) * (hash(k, 7) < .5 ? -1 : 1)
    const s = 4 + hash(k, 8) * 10, flat = .35 + .65 * Math.abs(Math.cos(age * 8 + k))
    ctx.globalAlpha = Math.min(1, (1 - age) * 1.6)
    ctx.fillStyle = hash(k, 9) < .3 ? 'rgb(58,50,96)' : 'rgb(22,18,40)'
    ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ctx.scale(1, flat)
    ctx.beginPath()
    ctx.moveTo(-s * (.5 + .3 * hash(k, 10)), -s * .3)
    ctx.lineTo(s * (.4 + .4 * hash(k, 11)), -s * (.4 + .2 * hash(k, 12)))
    ctx.lineTo(s * (.5 + .2 * hash(k, 13)), s * .35)
    ctx.lineTo(-s * (.3 + .3 * hash(k, 14)), s * (.3 + .3 * hash(k, 15)))
    ctx.closePath(); ctx.fill()
    ctx.restore()
  }
  ctx.restore()
}

// テレビの砂嵐 (2026-10-30 プロローグ・月喰いの王が消える所)。 粗いざらつきの絵を数枚だけ作っておき、 40ms ごとに替える。
//   ざらつきは灰色の点・横に流れる明るい帯・走査線の濃淡。 濃さ a で絵の上にかぶさり、 a=1 で絵は見えなくなる。
const SNOW_W = 240, SNOW_H = 135, SNOW_FRAMES = 6
let snow: HTMLCanvasElement[] | null = null
function snowFrames(): HTMLCanvasElement[] {
  if (snow || typeof document === 'undefined') return snow ?? []
  snow = []
  for (let f = 0; f < SNOW_FRAMES; f++) {
    const c = document.createElement('canvas'); c.width = SNOW_W; c.height = SNOW_H
    const g = c.getContext('2d'); if (!g) continue
    const d = g.createImageData(SNOW_W, SNOW_H)
    for (let y = 0; y < SNOW_H; y++) {
      const row = .82 + .18 * hash(y, f + 40)
      for (let x = 0; x < SNOW_W; x++) {
        const v = Math.floor(255 * row * hash(x + y * SNOW_W, f + 1) ** 1.3), i = (x + y * SNOW_W) * 4
        d.data[i] = v; d.data[i + 1] = v; d.data[i + 2] = v; d.data[i + 3] = 255
      }
    }
    g.putImageData(d, 0, 0)
    snow.push(c)
  }
  return snow
}
export function drawAdvStatic(ctx: CanvasRenderingContext2D, v: AdvStatic): void {
  if (v.a <= 0) return
  const frames = snowFrames(), W = ARENA.width, H = ARENA.height
  ctx.save()
  ctx.globalAlpha = v.a
  const f = frames[Math.floor(v.el / 40) % Math.max(1, frames.length)]
  if (f) {
    ctx.imageSmoothingEnabled = false
    // 1枚ごとに少し横へずらして、 同じ点が並んで見えないようにする。
    const ox = -Math.floor(hash(Math.floor(v.el / 40), 7) * 8) * W / SNOW_W
    ctx.drawImage(f, ox, 0, W + W / SNOW_W * 8, H)
  } else { ctx.fillStyle = '#777'; ctx.fillRect(0, 0, W, H) }
  // 下から上へ流れる明るい帯。
  const by = H - ((v.el * .45) % (H * 1.4))
  const band = ctx.createLinearGradient(0, by - 60, 0, by + 60)
  band.addColorStop(0, 'rgba(255,255,255,0)'); band.addColorStop(.5, `rgba(255,255,255,${.22})`); band.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = band; ctx.fillRect(0, by - 60, W, 120)
  // 走査線。
  ctx.fillStyle = 'rgba(0,0,0,.18)'
  for (let y = 0; y < H; y += 4) ctx.fillRect(0, y, W, 2)
  ctx.restore()
}
